// components/flow/components/config/AudioConfig.tsx
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Music, Volume2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow';
import MediaLibraryModal from '../MediaLibraryModal';
import RandomNextToggle from './shared/RandomNextToggle';

interface AudioConfigProps {
    node: FlowNode;
    onClose: () => void;
    pageId: string;
}

export default function AudioConfig({ node, onClose, pageId }: AudioConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [mediaUrl, setMediaUrl] = useState(node.data.config?.media_url || '');
    const [mediaFilename, setMediaFilename] = useState(node.data.config?.media_filename || '');
    const [attachmentId, setAttachmentId] = useState(node.data.config?.attachment_id || '');
    const [typingSeconds, setTypingSeconds] = useState(node.data.config?.delay_seconds || 0);
    const [isRandom, setIsRandom] = useState<boolean>(node.data.is_random ?? false);

    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

    const updateMutation = useMutation({
        mutationFn: () => updateNode(node.data.id, {
            node_name: nodeName,
            is_random: isRandom,
            config: {
                media_url: mediaUrl,
                media_filename: mediaFilename,
                media_type: 'audio',
                attachment_id: attachmentId || undefined,
                delay_seconds: typingSeconds || undefined,
            }
        }),
        onSuccess: () => {
            toast({
                title: 'Configurações salvas!',
                description: 'O áudio foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    const handleSave = () => {
        if (!mediaUrl) {
            toast({
                variant: 'destructive',
                title: 'Áudio obrigatório',
                description: 'Por favor, selecione um áudio.',
            });
            return;
        }

        updateMutation.mutate();
    };

    const handleMediaSelect = (media: any) => {
        setMediaUrl(media.file_url);
        setMediaFilename(media.filename);

        if (media.attachment_id) {
            setAttachmentId(media.attachment_id);
        } else {
            setAttachmentId('');
        }
    };

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Enviar Áudio"
                            className="mt-1.5"
                            disabled={updateMutation.isPending}
                        />
                    </div>

                    <div>
                        <Label>Áudio *</Label>
                        {mediaUrl ? (
                            <div className="mt-1.5 space-y-2">
                                <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg overflow-hidden border border-green-200 p-6">
                                    <div className="flex flex-col items-center gap-4">
                                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                                            <Volume2 className="w-8 h-8 text-white" />
                                        </div>
                                        <audio
                                            src={mediaUrl}
                                            controls
                                            className="w-full"
                                            preload="metadata"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex-1 min-w-0">
                                        <span className="text-sm text-gray-700 truncate block">
                                            {mediaFilename}
                                        </span>
                                        {attachmentId && (
                                            <span className="text-xs text-gray-500">
                                                ID: {attachmentId}
                                            </span>
                                        )}
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setIsMediaLibraryOpen(true)}
                                        disabled={updateMutation.isPending}
                                    >
                                        Trocar
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsMediaLibraryOpen(true)}
                                disabled={updateMutation.isPending}
                                className="mt-1.5 w-full aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Music className="w-12 h-12 text-gray-400" />
                                <span className="text-sm text-gray-600">Clique para selecionar áudio</span>
                            </button>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5" />
                            Digitando... (segundos)
                        </Label>
                        <Input
                            type="number"
                            min={0}
                            max={30}
                            value={typingSeconds}
                            onChange={(e) => setTypingSeconds(Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
                            placeholder="0"
                            className="h-10 w-32"
                            disabled={updateMutation.isPending}
                        />
                        <p className="text-xs text-gray-500">
                            Mostra "digitando..." por X segundos antes de enviar. 0 = desativado.
                        </p>
                    </div>

                    <RandomNextToggle
                        isRandom={isRandom}
                        onChange={setIsRandom}
                        disabled={updateMutation.isPending}
                    />

                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-xs text-green-900">
                            💡 <strong>Dica:</strong> Este áudio será enviado diretamente para o usuário sem texto adicional.
                        </p>
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
                    Cancelar
                </Button>
                <Button onClick={handleSave} disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Salvando...' : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Configurações
                        </>
                    )}
                </Button>
            </div>

            <MediaLibraryModal
                isOpen={isMediaLibraryOpen}
                onClose={() => setIsMediaLibraryOpen(false)}
                onSelect={handleMediaSelect}
                pageId={pageId}
                mediaType="audio"
            />
        </>
    );
}